"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import Link from "next/link";
import LandingFooter from "@/app/components/landing/LandingFooter";
import styles from "./EmbedAndAnalyticsClient.module.css";

/* ------------------------------------------------------------------ */
/*  Interactive demos                                                  */
/* ------------------------------------------------------------------ */

function EmbedPlayerDemo() {
  return (
    <div className={styles.embedDemo}>
      {/* Mock website frame */}
      <div className={styles.siteFrame}>
        <div className={styles.siteNav}>
          <div className={styles.siteLogo} />
          <div className={styles.siteLinks}>
            <span className={styles.siteLink} />
            <span className={styles.siteLink} />
            <span className={styles.siteLink} />
          </div>
        </div>
        <div className={styles.siteBody}>
          <div className={styles.siteTextGroup}>
            <div className={styles.textLine} style={{ width: "55%" }} />
            <div className={styles.textLine} style={{ width: "75%", opacity: 0.5 }} />
            <div className={styles.textLine} style={{ width: "40%", opacity: 0.5 }} />
          </div>
          {/* Embedded player */}
          <div className={styles.embeddedPlayer}>
            <div className={styles.playerPlayBtn}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                <polygon points="8 5 19 12 8 19 8 5" />
              </svg>
            </div>
            <div className={styles.playerControls}>
              <div className={styles.playerTimeline}>
                <div className={styles.playerProgress} />
              </div>
            </div>
          </div>
          <div className={styles.siteTextGroup}>
            <div className={styles.textLine} style={{ width: "80%", opacity: 0.4 }} />
            <div className={styles.textLine} style={{ width: "60%", opacity: 0.4 }} />
          </div>
        </div>
      </div>
      {/* Embed code */}
      <div className={styles.embedCode}>
        <span className={styles.codeTag}>&lt;iframe</span>
        <span className={styles.codeAttr}> src</span>
        <span className={styles.codeEq}>=</span>
        <span className={styles.codeStr}>&quot;flare.link/embed/...&quot;</span>
        <span className={styles.codeTag}> /&gt;</span>
      </div>
    </div>
  );
}

function PlayerCustomDemo() {
  const [activeTheme, setActiveTheme] = useState("simple");
  const themes = ["simple", "vsl", "clay"];
  const colors = ["#FF6702", "#3B82F6", "#22C55E", "#8B5CF6", "#EC4899", "#EF4444"];

  return (
    <div className={styles.playerCustomDemo}>
      {/* Player preview */}
      <div className={styles.playerPreview}>
        <div className={styles.previewContent}>
          <div className={styles.bigPlayBtn}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
              <polygon points="8 5 19 12 8 19 8 5" />
            </svg>
          </div>
        </div>
        <div className={styles.previewControls}>
          <span className={styles.previewControlDot} />
          <div className={styles.previewTimeline}>
            <div className={styles.previewTimelineFill} />
          </div>
          <span className={styles.previewControlDot} />
          <span className={styles.previewControlDot} />
        </div>
      </div>

      {/* Theme selector */}
      <div className={styles.themeRow}>
        {themes.map((t) => (
          <button
            key={t}
            className={`${styles.themeBtn} ${activeTheme === t ? styles.themeBtnActive : ""}`}
            onClick={() => setActiveTheme(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Color selector */}
      <div className={styles.colorRow}>
        {colors.map((c) => (
          <span key={c} className={styles.colorDot} style={{ background: c }} />
        ))}
        <span className={styles.colorLabel}>Accent</span>
      </div>
    </div>
  );
}

function AnalyticsDashDemo() {
  const bars = [30, 48, 42, 65, 78, 70, 88, 62, 75, 82, 55, 45];

  return (
    <div className={styles.analyticsDemo}>
      {/* Stat cards */}
      <div className={styles.statRow}>
        <div className={styles.stat}>
          <span className={styles.statValue}>2,847</span>
          <span className={styles.statLabel}>Total views</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>68%</span>
          <span className={styles.statLabel}>Avg. watched</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>4m 12s</span>
          <span className={styles.statLabel}>Avg. duration</span>
        </div>
      </div>

      {/* Chart */}
      <div className={styles.chart}>
        <div className={styles.chartBars}>
          {bars.map((h, i) => (
            <div key={i} className={styles.chartBar} style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className={styles.chartBaseline} />
      </div>

      {/* Engagement heatmap (simplified) */}
      <div className={styles.heatmapRow}>
        <span className={styles.heatmapLabel}>Engagement</span>
        <div className={styles.heatmap}>
          <div className={styles.heatmapFill} />
        </div>
      </div>

      {/* Geography */}
      <div className={styles.geoRow}>
        <span className={styles.geoItem}><span className={styles.geoDot} style={{ background: "#FF6702" }} /> US 42%</span>
        <span className={styles.geoItem}><span className={styles.geoDot} style={{ background: "rgba(255,103,2,0.6)" }} /> UK 18%</span>
        <span className={styles.geoItem}><span className={styles.geoDot} style={{ background: "rgba(255,103,2,0.35)" }} /> DE 12%</span>
        <span className={styles.geoItem}><span className={styles.geoDot} style={{ background: "rgba(255,103,2,0.2)" }} /> Other</span>
      </div>
    </div>
  );
}

function UploadDemo() {
  return (
    <div className={styles.uploadDemo}>
      <div className={styles.uploadZone}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <span className={styles.uploadText}>Drop videos to upload</span>
        <span className={styles.uploadSub}>MP4, MOV, WebM — no size limit</span>
      </div>
      <div className={styles.uploadList}>
        <div className={styles.uploadItem}>
          <div className={styles.uploadThumb} />
          <div className={styles.uploadMeta}>
            <span className={styles.uploadName}>Product demo.mp4</span>
            <div className={styles.uploadBar}><div className={styles.uploadBarFill} style={{ width: "100%" }} /></div>
          </div>
          <span className={styles.uploadDone}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          </span>
        </div>
        <div className={styles.uploadItem}>
          <div className={styles.uploadThumb} />
          <div className={styles.uploadMeta}>
            <span className={styles.uploadName}>Onboarding flow.mov</span>
            <div className={styles.uploadBar}><div className={styles.uploadBarFill} style={{ width: "65%" }} /></div>
          </div>
          <span className={styles.uploadPct}>65%</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Extras data                                                        */
/* ------------------------------------------------------------------ */

const extras = [
  { title: "Unlimited Uploads", desc: "Upload as many videos as you need. No per-video fees." },
  { title: "1000GB Free Bandwidth", desc: "Generous monthly bandwidth included with every plan." },
  { title: "Custom Thumbnails", desc: "Set a custom thumbnail or let Flare pick the best frame." },
  { title: "Autoplay & Loop", desc: "Configure videos to autoplay, loop, or start muted on page load." },
  { title: "CTA Overlays", desc: "Add clickable call-to-action buttons at any point in the video." },
  { title: "Domain Whitelisting", desc: "Restrict where your embeds can be played for content control." },
];

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function EmbedAndAnalyticsClient() {
  const cardsRef = useRef([]);
  const headerRef = useRef(null);
  const extrasRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add(styles.visible);
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    cardsRef.current.forEach((el) => el && observer.observe(el));
    if (extrasRef.current) observer.observe(extrasRef.current);

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = useCallback((e) => {
    cardsRef.current.forEach((card) => {
      if (!card) return;
      const rect = card.getBoundingClientRect();
      card.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
      card.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    });
  }, []);

  return (
    <div className={styles.page}>
      <main>
        {/* Hero */}
        <section className={styles.hero}>
          <div className={styles.ambientPrimary} />
          <div className={styles.ambientSecondary} />
          <div className={styles.heroContent} ref={headerRef}>
            <span className={styles.badge}>Embed & Analytics</span>
            <h1 className={styles.heroTitle}>
              Embed your videos.
              <br />
              Know who&apos;s watching.
            </h1>
            <p className={styles.heroSubtitle}>
              A customisable video player you can embed anywhere on the web, backed by detailed analytics that show you exactly how viewers engage.
            </p>
            <div className={styles.heroCtas}>
              <Link href="/register?plan=embed" className={styles.btnPrimary}>
                Get Started
              </Link>
              <Link href="/#pricing" className={styles.btnSecondary}>
                View Pricing
              </Link>
            </div>
          </div>
        </section>

        {/* Bento feature grid */}
        <section className={styles.features}>
          <div className={styles.container} onMouseMove={handleMouseMove}>
            <div className={styles.bentoGrid}>
              {/* Card 1 — Embed anywhere (wide) */}
              <div
                className={`${styles.card} ${styles.cardEmbed}`}
                ref={(el) => (cardsRef.current[0] = el)}
                style={{ transitionDelay: "0ms" }}
              >
                <div className={styles.cardText}>
                  <h2 className={styles.cardTitle}>Embed Videos Anywhere</h2>
                  <p className={styles.cardDesc}>
                    Drop a Flare player into any website, landing page, or sales funnel with a single embed code. Responsive, fast-loading, and beautiful on every device.
                  </p>
                </div>
                <div className={styles.cardDemo}>
                  <EmbedPlayerDemo />
                </div>
              </div>

              {/* Card 2 — Analytics (wide) */}
              <div
                className={`${styles.card} ${styles.cardAnalytics}`}
                ref={(el) => (cardsRef.current[1] = el)}
                style={{ transitionDelay: "80ms" }}
              >
                <div className={styles.cardText}>
                  <h2 className={styles.cardTitle}>Detailed Video Analytics</h2>
                  <p className={styles.cardDesc}>
                    Track views, watch time, engagement curves, and viewer geography in real time. Know where viewers drop off and what keeps them watching.
                  </p>
                </div>
                <div className={styles.cardDemo}>
                  <AnalyticsDashDemo />
                </div>
              </div>

              {/* Card 3 — Player customisation */}
              <div
                className={`${styles.card} ${styles.cardPlayer}`}
                ref={(el) => (cardsRef.current[2] = el)}
                style={{ transitionDelay: "160ms" }}
              >
                <div className={styles.cardText}>
                  <h2 className={styles.cardTitle}>Customisable Player</h2>
                  <p className={styles.cardDesc}>
                    Multiple themes, brand accent colors, and 20+ toggleable controls. From clean VSL to full-featured — you decide.
                  </p>
                </div>
                <div className={styles.cardDemo}>
                  <PlayerCustomDemo />
                </div>
              </div>

              {/* Card 4 — Upload */}
              <div
                className={`${styles.card} ${styles.cardUpload}`}
                ref={(el) => (cardsRef.current[3] = el)}
                style={{ transitionDelay: "240ms" }}
              >
                <div className={styles.cardText}>
                  <h2 className={styles.cardTitle}>Upload Any Video</h2>
                  <p className={styles.cardDesc}>
                    Drag and drop any video file — MP4, MOV, WebM. No size limits, no compression. Your videos, exactly as you made them.
                  </p>
                </div>
                <div className={styles.cardDemo}>
                  <UploadDemo />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Extra features */}
        <section className={styles.extrasSection} ref={extrasRef}>
          <div className={styles.container}>
            <h2 className={styles.extrasTitle}>Everything you need to embed and track</h2>
            <div className={styles.extrasGrid}>
              {extras.map((item) => (
                <div key={item.title} className={styles.extraCard}>
                  <h3 className={styles.extraCardTitle}>{item.title}</h3>
                  <p className={styles.extraCardDesc}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className={styles.cta}>
          <div className={styles.ctaGlow} />
          <div className={styles.ctaInner}>
            <h2 className={styles.ctaTitle}>Ready to embed?</h2>
            <p className={styles.ctaDesc}>
              Upload your first video and have it embedded on your site in minutes.
            </p>
            <Link href="/register?plan=embed" className={styles.btnPrimary}>
              Get Started Free
            </Link>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
